import type {
  ListingCardItem,
  ListingSortOptionId,
  ListingType,
  ListingsCardSection,
} from "../../../types/pages/listings";

export type ActiveListingType = "all" | ListingType;

export type ListingsSearchState = {
  query: string;
  activeType: ActiveListingType;
  activeCategory: string;
  sortBy: ListingSortOptionId;
  page: number;
};

export const DEFAULT_ACTIVE_LISTING_TYPE: ActiveListingType = "persona";
const DEFAULT_PAGE = 1;

const VALID_ACTIVE_TYPES = new Set<ActiveListingType>([
  "all",
  "persona",
  "skill",
]);

export const sortItems = (
  items: ListingCardItem[],
  sortBy: ListingSortOptionId
) => {
  const nextItems = [...items];

  if (sortBy === "popular") {
    return nextItems.sort((a, b) => b.popularity_score - a.popularity_score);
  }

  if (sortBy === "newest") {
    return nextItems.sort((a, b) => {
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
  }

  if (sortBy === "price_low") {
    return nextItems.sort((a, b) => a.price - b.price);
  }

  if (sortBy === "price_high") {
    return nextItems.sort((a, b) => b.price - a.price);
  }

  return nextItems;
};

export const filterAndSortListingItems = ({
  items,
  query,
  activeType,
  activeCategory,
  categoryMap,
  sortBy,
}: {
  items: ListingCardItem[];
  query: string;
  activeType: ActiveListingType;
  activeCategory: string;
  categoryMap: Map<string, string>;
  sortBy: ListingSortOptionId;
}) => {
  const normalizedQuery = query.trim().toLowerCase();

  const filtered = items.filter((item) => {
    const inType = activeType === "all" || item.type === activeType;
    const inCategory = activeCategory === "all" || item.category === activeCategory;

    if (!inType || !inCategory) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    const categoryLabel = (categoryMap.get(item.category) || "").toLowerCase();
    const searchText = [
      item.title,
      item.subtitle,
      item.description,
      item.author_name,
      categoryLabel,
    ]
      .join(" ")
      .toLowerCase();

    return searchText.includes(normalizedQuery);
  });

  return sortItems(filtered, sortBy);
};

const getFirstValue = (value?: string | string[]) => {
  if (Array.isArray(value)) {
    return value[0] ?? "";
  }

  return value ?? "";
};

const parsePageNumber = (value?: string | string[]) => {
  const parsed = Number.parseInt(getFirstValue(value), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PAGE;
};

export const getListingsSearchState = (
  searchParams: Record<string, string | string[] | undefined>,
  section: Pick<
    ListingsCardSection,
    "categories" | "default_sort" | "sort_options"
  >
): ListingsSearchState => {
  const activeTypeInput = getFirstValue(searchParams.type).trim();
  const activeType = VALID_ACTIVE_TYPES.has(activeTypeInput as ActiveListingType)
    ? (activeTypeInput as ActiveListingType)
    : DEFAULT_ACTIVE_LISTING_TYPE;
  const categoryIds = new Set(section.categories.map((category) => category.id));
  const activeCategoryInput = getFirstValue(searchParams.category).trim();
  const activeCategory =
    activeCategoryInput && categoryIds.has(activeCategoryInput)
      ? activeCategoryInput
      : "all";
  const sortIds = new Set(section.sort_options.map((option) => option.id));
  const sortInput = getFirstValue(searchParams.sort).trim();
  const sortBy = sortIds.has(sortInput as ListingSortOptionId)
    ? (sortInput as ListingSortOptionId)
    : section.default_sort;

  return {
    query: getFirstValue(searchParams.q).trim(),
    activeType,
    activeCategory,
    sortBy,
    page: parsePageNumber(searchParams.page),
  };
};

export const buildListingsSearchParams = (
  state: ListingsSearchState,
  section: Pick<ListingsCardSection, "default_sort">,
  overrides: Partial<ListingsSearchState> = {}
) => {
  const nextState = {
    ...state,
    ...overrides,
  };
  const params = new URLSearchParams();

  if (nextState.query) {
    params.set("q", nextState.query);
  }

  if (nextState.activeType !== DEFAULT_ACTIVE_LISTING_TYPE) {
    params.set("type", nextState.activeType);
  }

  if (nextState.activeCategory !== "all") {
    params.set("category", nextState.activeCategory);
  }

  if (nextState.sortBy !== section.default_sort) {
    params.set("sort", nextState.sortBy);
  }

  if (nextState.page > DEFAULT_PAGE) {
    params.set("page", String(nextState.page));
  }

  return params;
};
